import { Alert, Box, Stack, Typography } from "@mui/material"
import { FC, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { sl1ProviderState, sl1Remaining } from "shared/sl1ImportData"
import {
  Sl1ImportFleetDto,
  useGetSl1ImportFleetQuery,
} from "src/features/things/sl1ImportApi"
import { Sl1FleetMeter, Sl1Tile, fmtNum } from "./Sl1ImportChrome"
import Sl1ImportFleetTable from "./Sl1ImportFleetTable"

/**
 * Screen 1 — the whole operation on one screen (LOCKER-382 §D-4).
 *
 * Six rollup tiles, the fleet meter, then one row per provider. **One Query, no S3
 * read, and no `orders` aggregation** — which is the entire reason `sl1ImportFleet`
 * exists as a stored snapshot rather than being computed on demand. The cost is
 * staleness, and `scannedAt` is on the face of the screen so it is the reader's to
 * judge rather than the page's to hide.
 */

type Rollup = {
  providers: number
  unmapped: number
  sl1Rows: number
  matchedInSl2: number
  imported: number
  remaining: number
}

/**
 * Fold the fleet into the tile row.
 *
 * `matchedInSl2` skips unmapped providers deliberately: they have no SL2 tenant, so
 * their stored figure is a carried-forward zero rather than a measurement, and
 * summing it would present "already in SL2" as smaller than it is for a reason
 * nobody could see on the screen.
 */
export const rollupFleet = (fleet: Sl1ImportFleetDto[]): Rollup =>
  fleet.reduce<Rollup>(
    (acc, f) => {
      const mapped = f.sl2TenantId !== null
      return {
        providers: acc.providers + 1,
        unmapped: acc.unmapped + (mapped ? 0 : 1),
        sl1Rows: acc.sl1Rows + f.sl1Rows,
        matchedInSl2: acc.matchedInSl2 + (mapped ? f.matchedInSl2 : 0),
        imported: acc.imported + f.imported,
        remaining: acc.remaining + sl1Remaining(f),
      }
    },
    {
      providers: 0,
      unmapped: 0,
      sl1Rows: 0,
      matchedInSl2: 0,
      imported: 0,
      remaining: 0,
    },
  )

export const Sl1ImportOverview: FC = () => {
  const { t } = useTranslation()
  const { data, isLoading, error } = useGetSl1ImportFleetQuery({ limit: 500 })

  const fleet = useMemo(
    () =>
      [...(data?.things ?? [])].sort((a, b) =>
        a.providerName.localeCompare(b.providerName),
      ),
    [data],
  )
  const roll = useMemo(() => rollupFleet(fleet), [fleet])

  // The freshest scan across the estate. The oldest would be the more conservative
  // reading, but the meter is an "as of" statement about the last refresh, and the
  // per-row `scannedAt` is where a stale individual provider shows up.
  const newestScan = useMemo(
    () =>
      fleet.reduce<string | undefined>(
        (acc, f) => (!acc || f.scannedAt > acc ? f.scannedAt : acc),
        undefined,
      ),
    [fleet],
  )

  const needsAttention = useMemo(
    () =>
      fleet.filter(
        (f) =>
          sl1ProviderState({
            sl2TenantId: f.sl2TenantId,
            latestJobStatus: f.lastJobStatus,
            latestJobKind: f.lastJobKind,
            imported: f.imported,
            remaining: sl1Remaining(f),
          }) === "BLOCKED",
      ).length,
    [fleet],
  )

  if (isLoading) {
    return <Typography variant="body2">{t("sl1LoadingFleet")}</Typography>
  }
  if (error) {
    return <Alert severity="error">{t("sl1FleetLoadFailed")}</Alert>
  }

  return (
    <Stack data-ui="UI-SL2W-171" spacing={3}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
          gap: 1.5,
        }}
      >
        <Sl1Tile
          label={t("sl1TileProviders")}
          value={fmtNum(roll.providers)}
          hint={t("sl1TileProvidersHint")}
        />
        <Sl1Tile
          label={t("sl1TileUnmapped")}
          value={fmtNum(roll.unmapped)}
          hint={t("sl1TileUnmappedHint")}
          dotColor="text.disabled"
        />
        <Sl1Tile
          label={t("sl1TileSl1Rows")}
          value={fmtNum(roll.sl1Rows)}
          hint={t("sl1TileSl1RowsHint")}
        />
        <Sl1Tile
          label={t("sl1TileInSl2")}
          value={fmtNum(roll.matchedInSl2)}
          hint={t("sl1TileInSl2Hint")}
          dotColor="success.main"
        />
        <Sl1Tile
          label={t("sl1TileImported")}
          value={fmtNum(roll.imported)}
          hint={t("sl1TileImportedHint")}
          dotColor="secondary.main"
        />
        <Sl1Tile
          label={t("sl1TileRemaining")}
          value={fmtNum(roll.remaining)}
          hint={t("sl1TileRemainingHint")}
          dotColor="info.main"
        />
      </Box>

      <Sl1FleetMeter
        imported={roll.imported}
        matched={roll.matchedInSl2}
        found={roll.sl1Rows}
        scannedAt={newestScan}
      />

      {roll.unmapped > 0 && (
        // Stated once, at the top, rather than only as N red rows further down. An
        // unmapped provider cannot be imported at all, and that is a decision to go
        // and make (Decision 1) rather than a run to go and fix.
        <Alert severity="info" variant="outlined">
          {t("sl1UnmappedBanner", { n: roll.unmapped })}
        </Alert>
      )}
      {needsAttention > 0 && (
        // `error`, not `warning`. Two reasons, and the second is the real one:
        // a blocked provider IS stop-work, so red is the honest severity; and
        // MUI's `warning` palette is amber, which the repo bans as a fill
        // (`no-harsh-yellow-ui`). Red here also matches the stripe on the
        // blocked provider's own row below, so the banner and the row agree.
        <Alert severity="error" variant="outlined">
          {t("sl1BlockedBanner", { n: needsAttention })}
        </Alert>
      )}

      <Stack spacing={1}>
        <Typography sx={{ fontWeight: 700, fontSize: "0.95rem" }}>
          {t("sl1ByProvider")}
        </Typography>
        <Typography variant="body2" sx={{ color: "text.secondary", maxWidth: "78ch" }}>
          {t("sl1ByProviderHint")}
        </Typography>
        <Sl1ImportFleetTable fleet={fleet} />
      </Stack>
    </Stack>
  )
}

export default Sl1ImportOverview
